import { useState, useCallback } from 'react';
import { playBeep } from '../../../utils/audio';

export const useLabelPrint = ({
  printType,
  invoiceData,
  eanValidated,
  bridgeConnected,
  labelFormat,
  selectedPrinter,
  fetchLabel,
  printToBridge,
  setPrinterModalOpen,
  resetEans,
  inputRef,
  setNfeKey,
  onPrintSuccess,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [forcePrint, setForcePrint] = useState(false);
  const [showForceDialog, setShowForceDialog] = useState(false);
  const [labelError, setLabelError] = useState(null);

  const handlePrintLabel = useCallback(
    async (retry = false, skipForceCheck = false) => {
      if (isPrinting) return;

      let missingGtin = false;
      let missingValidation = false;
      if (
        printType === "detalhada" &&
        invoiceData &&
        invoiceData.shipping &&
        Array.isArray(invoiceData.shipping.items_gtin)
      ) {
        invoiceData.shipping.items_gtin.forEach((item, idx) => {
          if (!item.gtin_code) missingGtin = true;
          if (item.gtin_code && !eanValidated[idx]) missingValidation = true;
        });
      }

      if (
        printType === "detalhada" &&
        invoiceData &&
        invoiceData.shipping &&
        Array.isArray(invoiceData.shipping.items_gtin) &&
        (missingGtin || missingValidation) &&
        !forcePrint &&
        !skipForceCheck &&
        !showForceDialog
      ) {
        setShowForceDialog(true);
        playBeep(200, 0.3, "error");
        return;
      }

      setIsPrinting(true);
      try {
        setLabelError(null);

        if (!bridgeConnected && labelFormat === "zpl") {
          setLabelError("Bridge não conectado. Instale e execute o Wala Print Bridge.");
          playBeep(200, 0.3, "error");
          setIsPrinting(false);
          if (!retry) {
            setTimeout(() => handlePrintLabel(true, skipForceCheck), 500);
          }
          return;
        }

        if (!selectedPrinter && labelFormat === "zpl") {
          setPrinterModalOpen(true);
          setIsPrinting(false);
          if (!retry) {
            setTimeout(() => handlePrintLabel(true, skipForceCheck), 500);
          }
          return;
        }

        const result = await fetchLabel();
        if (result.data) {
          let sucesso = false;
          if (labelFormat === "zpl") {
            let zplData;
            if (result.data instanceof Blob) {
              zplData = await result.data.text();
            } else {
              zplData = result.data;
            }
            await printToBridge(zplData);
            playBeep(1200, 0.2, "success");
            sucesso = true;
          } else {
            const blob = result.data instanceof Blob 
              ? result.data 
              : new Blob([result.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");
            playBeep(1200, 0.2, "success");
            sucesso = true;
          }
          setLabelError(null);
          
          if (sucesso) {
            // Chama o callback de sucesso antes de limpar os dados
            if (onPrintSuccess) {
              onPrintSuccess();
            }
            
            setNfeKey("");
            resetEans();
            setForcePrint(false);
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
              }
            }, 100);
          }
        }
      } catch (error) {
        playBeep(200, 0.3, "error");
        if (
          error.message.includes("despachado") ||
          error.message.includes("404")
        ) {
          setLabelError("Este pedido já foi despachado e não é possível gerar etiqueta.");
        } else {
          setLabelError(`Erro na impressão: ${error.message}`);
        }
      } finally {
        setTimeout(() => setIsPrinting(false), 500);
      }
    },
    [
      isPrinting,
      printType,
      invoiceData,
      eanValidated,
      forcePrint,
      showForceDialog,
      bridgeConnected,
      labelFormat,
      selectedPrinter,
      fetchLabel,
      printToBridge,
      setPrinterModalOpen,
      resetEans,
      inputRef,
      setNfeKey,
      onPrintSuccess,
    ]
  );

  const handleCloseForceDialog = () => {
    setShowForceDialog(false);
    setForcePrint(false);
  };

  const handleConfirmForceDialog = () => {
    setShowForceDialog(false);
    setForcePrint(true);
    setTimeout(() => handlePrintLabel(false, true), 100);
  };

  return {
    isPrinting,
    forcePrint,
    showForceDialog,
    labelError,
    handlePrintLabel,
    handleCloseForceDialog,
    handleConfirmForceDialog,
  };
};